import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { BrandWord } from "@/components/Brand";
import { adminApi, adminToken } from "@/admin/adminApi";

const AdminCtx = createContext(null);
export const useAdmin = () => useContext(AdminCtx);
export const can = (admin, ...roles) => admin?.role === "super_admin" || roles.includes(admin?.role);

const ROLE_LABEL = { super_admin: "Super-admin", finance: "Finance", support: "Support", readonly: "Lecture seule" };
const NAV = [
  ["", "Tableau de bord"], ["players", "Joueurs"], ["kyc", "KYC"], ["games", "Dés"], ["ludo", "Ludo"], ["dames", "Dames"], ["chat", "Chat"], ["referral", "Parrainage"], ["finance", "Finances"],
  ["settings", "Réglages"], ["announcements", "Annonces"], ["audit", "Journal d'audit", ["super_admin"]], ["admins", "Admins", ["super_admin"]],
];
const IDLE_MS = 30 * 60 * 1000;

export default function AdminLayout() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [failed, setFailed] = useState(!adminToken.get());

  useEffect(() => {
    if (!adminToken.get()) return;
    adminApi.get("/auth/me").then((r) => setAdmin(r.data)).catch(() => setFailed(true));
  }, []);

  useEffect(() => {
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        adminToken.clear();
        navigate("/admin/login");
      }, IDLE_MS);
    };
    const evs = ["mousemove", "keydown", "touchstart", "click"];
    evs.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(timer);
      evs.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [navigate]);

  if (failed) return <Navigate to="/admin/login" replace />;
  if (!admin) return <div className="min-h-screen bg-[#0B0D12] p-10 text-slate-500">Chargement…</div>;
  const logout = async () => {
    await adminApi.post("/auth/logout").catch(() => {});
    adminToken.clear();
    navigate("/admin/login");
  };
  return (
    <AdminCtx.Provider value={admin}>
      <div className="min-h-screen bg-[#0B0D12] text-slate-100 md:flex" data-testid="admin-layout">
        <aside className="border-b border-white/10 bg-[#0f121a] md:min-h-screen md:w-60 md:border-b-0 md:border-r">
          <div className="flex items-center justify-between gap-2 p-4">
            <div className="min-w-0">
              <p className="font-display font-bold"><BrandWord /> · Admin</p>
              <p className="truncate text-xs text-slate-500" data-testid="admin-email">{admin.email}</p>
              <p className="text-xs font-medium text-amber-400" data-testid="admin-role">{ROLE_LABEL[admin.role]}</p>
            </div>
            <button onClick={logout} className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/10" data-testid="admin-logout-btn" aria-label="logout"><LogOut className="h-4 w-4" /></button>
          </div>
          <nav className="flex gap-1 overflow-x-auto px-2 pb-2 md:flex-col md:overflow-visible">
            {NAV.filter(([, , r]) => !r || r.includes(admin.role)).map(([to, label]) => (
              <NavLink key={to} to={`/admin/${to}`} end={to === ""} data-testid={`admin-nav-${to || "dashboard"}`}
                className={({ isActive }) => `shrink-0 rounded-lg px-3 py-2.5 text-sm ${isActive ? "bg-amber-500/15 text-amber-300" : "text-slate-400 hover:text-white"}`}>
                {label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1 p-4 md:p-8"><Outlet /></main>
      </div>
    </AdminCtx.Provider>
  );
}
